import type { Vec2 } from '../../geo/types';
import { distance } from './streetGraph';
import type { LifeEntrance, LifeProp, LifeStep } from './types';

/** A door a citizen can reach: the spot before it and the street-graph node that spot stands by. */
export interface Access {
  readonly entry: LifeEntrance;
  readonly approach: Vec2;
  readonly node: number;
}

export const move = (p: Vec2, dir: Vec2, amount: number): Vec2 => [
  p[0] + dir[0] * amount,
  p[1] + dir[1] * amount,
];
export const heading = (a: Vec2, b: Vec2): number => Math.atan2(b[1] - a[1], b[0] - a[0]);

/*
 * The same path without the points that lie on the straight line between the points kept either
 * side of them. A path sampled every few metres down a straight street is one step, which is what
 * the plan stores and what each frame walks through to find where a citizen is.
 */
export function corners(points: readonly Vec2[], tolerance = 0.002): readonly Vec2[] {
  if (points.length < 3) return points;
  const kept: Vec2[] = [points[0]!];
  let from = 0;
  for (let to = 2; to < points.length; to++) {
    const a = points[from]!,
      b = points[to]!,
      length = Math.max(distance(a, b), 1e-9);
    const straight = points
      .slice(from + 1, to)
      .every(
        (p) =>
          Math.abs((b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0])) / length <=
          tolerance
      );
    if (straight) continue;
    kept.push(points[to - 1]!);
    from = to - 1;
  }
  kept.push(points.at(-1)!);
  return kept;
}

export function travel(
  steps: LifeStep[],
  points: readonly Vec2[],
  speed: number,
  prop?: LifeProp
): void {
  for (let i = 1; i < points.length; i++) {
    const from = points[i - 1]!,
      to = points[i]!,
      length = distance(from, to);
    if (length < 0.01) continue;
    steps.push({
      from,
      to,
      seconds: length / speed,
      action: 'walk',
      heading: heading(from, to),
      ...(prop ? { prop } : {}),
    });
  }
}
export function doorTravel(
  steps: LifeStep[],
  at: Access,
  leaving: boolean,
  speed: number,
  prop?: LifeProp
): void {
  const inside = move(at.entry.position, at.entry.outward, -0.35);
  const from = leaving ? inside : at.approach,
    to = leaving ? at.approach : inside;
  steps.push({
    from,
    to,
    seconds: distance(from, to) / (speed * 0.7),
    action: 'walk',
    heading: heading(from, to),
    door: at.entry.id,
    ...(prop ? { prop } : {}),
  });
}
export function stay(
  steps: LifeStep[],
  at: Access,
  seconds: number,
  action: 'inside' | 'wait' | 'work'
): void {
  const position =
    action === 'inside' ? move(at.entry.position, at.entry.outward, -0.35) : at.approach;
  steps.push({
    from: position,
    to: position,
    seconds,
    action,
    heading: Math.atan2(-at.entry.outward[1], -at.entry.outward[0]),
    ...(action === 'inside' ? { door: at.entry.id } : {}),
  });
}
